"use client";

import { ChevronDown } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { buttonVariants } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { Link, usePathname } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { iconsMaping } from "@/services/constants/sidebar";
import { Options } from "@/services/redux/sidebar.slice";

interface NavProps {
  isCollapsed: boolean;
  links: Options[];
}

export function Nav({ links, isCollapsed }: NavProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const s = searchParams.get("s");

  const [activeIndex, setActiveIndex] = useState<number | undefined>(0);

  useEffect(() => {
    if (pathname.includes("dashboard")) {
      setActiveIndex(0);
    } else if (pathname.includes("smart-allocation")) {
      setActiveIndex(1);
    } else if (pathname.includes("trade")) {
      setActiveIndex(2);
    } else if (pathname.includes("settings")) {
      setActiveIndex(6);
    } else {
      setActiveIndex(undefined);
    }

    return () => {
      setActiveIndex(0);
    };
  }, [pathname]);

  return (
    <TooltipProvider>
      <div
        data-collapsed={isCollapsed}
        className="group flex flex-col gap-4 px-2"
      >
        <nav className="grid gap-1 group-[[data-collapsed=true]]:justify-center group-[[data-collapsed=true]]:px-2">
          <Accordion
            type="single"
            collapsible
            className="flex flex-col space-y-2"
            value={`item-${activeIndex}`}
          >
            {links.map((link, index) => {
              const isActive = pathname === link.href;
              const Icon: any = iconsMaping(link.icon);
              return isCollapsed ? (
                <Tooltip key={index} delayDuration={0}>
                  <TooltipTrigger>
                    <Link
                      href={s ? link.href + `?s=${s}` : link.href}
                      className={cn(
                        buttonVariants({
                          variant: isActive ? "default" : "ghost",
                          size: "icon",
                        }),
                        "h-9 w-9",
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="sr-only">{link.title}</span>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="mb-4">
                    {link.title}
                    {link.label && (
                      <span className="ml-auto text-muted-foreground">
                        {link.label}
                      </span>
                    )}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border-b-0"
                >
                  <div className="flex items-center gap-1">
                    <Link
                      href={s ? link.href + `?s=${s}` : link.href}
                      className={cn(
                        buttonVariants({
                          variant: isActive ? "secondary" : "ghost",
                          size: "sm",
                        }),
                        "justify-start px-1 flex-1 overflow-hidden w-full hover:bg-transparent border hover:border-secondary border-sidebar ",
                      )}
                    >
                      <Icon className="mr-2 min-w-4 h-4" />
                      {link.title}
                      {link.label && (
                        <span
                          className={cn(
                            "ml-auto",
                            isActive && "text-background",
                          )}
                        >
                          {link.label}
                        </span>
                      )}
                    </Link>
                    {link.subMenu ? (
                      <AccordionTrigger
                        className="p-0"
                        onClick={() =>
                          setActiveIndex((st) => {
                            if (st === index) return undefined;
                            else return index;
                          })
                        }
                      >
                        <Link
                          href={"#"}
                          className={cn(
                            buttonVariants({
                              variant: "ghost",
                              size: "sm",
                            }),
                            "h-[36px] w-[28px]  p-0 hover:bg-transparent hover:border ",
                          )}
                        >
                          <ChevronDown
                            className={cn(
                              "w-4 h-4 text-current transition-transform",
                              activeIndex === index ? "rotate-180" : "",
                            )}
                          />
                        </Link>
                      </AccordionTrigger>
                    ) : (
                      <></>
                    )}
                  </div>
                  <AccordionContent className="ps-9 space-y-1 pt-1 pb-0">
                    {link.subMenu?.map((item) => {
                      const isActive = pathname === item.href;
                      // const Icon: any = iconsMaping(item.icon);
                      return (
                        <Link
                          key={item.title}
                          href={s ? item.href + `?s=${s}` : item.href}
                          className={cn(
                            buttonVariants({
                              variant: isActive ? "secondary" : "ghost",
                              size: "lg",
                            }),
                            "justify-start flex-1 overflow-hidden px-2 h-auto py-1 w-full hover:bg-transparent border hover:border-inherit border-transparent",
                          )}
                        >
                          {/* <Icon className="mr-2 min-w-4 h-4" /> */}
                          {item.title}
                          {item.label && (
                            <span
                              className={cn(
                                "ml-auto",
                                isActive && "text-background",
                              )}
                            >
                              {item.label}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </nav>
      </div>
    </TooltipProvider>
  );
}
